import clsx from 'clsx';
import type { ComponentPropsWithoutRef, ElementType, ReactElement, ReactNode } from 'react';

import { RecessedSurface } from '../recessed-surface/RecessedSurface';
import { getTertiaryPressedSurfaceClasses } from '../surface/surfaceClasses';

export type ButtonColor = 'default' | 'danger' | 'muted' | 'secondary';
export type ButtonIconPosition = 'both' | 'leading' | 'trailing';
export type ButtonVariant = 'destructive' | 'link' | 'primary' | 'secondary' | 'tertiary';
type LegacyButtonVariant = 'default' | 'solid';

type ButtonVariantValue = ButtonVariant | LegacyButtonVariant;
type InternalButtonVariant = ButtonVariant | 'muted';

type ButtonSharedProps<E extends ElementType = 'button'> = {
    readonly activeSurface?: 'default' | 'recessed';
    readonly as?: E;
    readonly children: ReactNode;
    readonly className?: string;
    readonly disabled?: boolean;
    readonly disabledVariant?: 'dark' | 'light';
    readonly iconOnly?: boolean;
    readonly iconPosition?: ButtonIconPosition;
    readonly isActive?: boolean;
    readonly size?: 'sm';
    readonly type?: 'button' | 'reset' | 'submit';
};

type ButtonOwnProps<
    E extends ElementType = 'button',
    V extends ButtonVariantValue | undefined = ButtonVariantValue | undefined,
> = ButtonSharedProps<E> & {
    // Compatibility bridge for the former `variant="solid" color="..."` API.
    readonly color?: ButtonColor;
    // Supports canonical semantic values plus legacy values for gradual migration.
    readonly variant?: V;
};

export type ButtonProps<
    E extends ElementType = 'button',
    V extends ButtonVariantValue | undefined = ButtonVariantValue | undefined,
> = ButtonOwnProps<E, V> & Omit<ComponentPropsWithoutRef<E>, keyof ButtonOwnProps<E>>;

/* ============================================
 * Size classes: padding + typography
 * ============================================ */
const getSmContentClasses = (iconPosition: ButtonIconPosition | undefined): string =>
    clsx(
        'py-s px-l',
        iconPosition == null && 'gap-s',
        iconPosition === 'leading' && 'gap-xs pl-m',
        iconPosition === 'trailing' && 'gap-xs pr-m',
        iconPosition === 'both' && 'gap-xs pl-m pr-m',
        iconPosition == null &&
            '[&:has(>svg:first-child)]:gap-xs [&:has(>svg:first-child)]:pl-m [&:has(>svg:last-child)]:gap-xs [&:has(>svg:last-child)]:pr-m',
    );

const getSmRecessedContentClasses = (iconPosition: ButtonIconPosition | undefined): string =>
    clsx(
        'py-[calc(var(--spacing-s)-var(--spacing-xxs))] px-[calc(var(--spacing-l)-var(--spacing-xxs))]',
        iconPosition == null && 'gap-s',
        iconPosition === 'leading' && 'gap-xs pl-[calc(var(--spacing-m)-var(--spacing-xxs))]',
        iconPosition === 'trailing' && 'gap-xs pr-[calc(var(--spacing-m)-var(--spacing-xxs))]',
        iconPosition === 'both' &&
            'gap-xs pl-[calc(var(--spacing-m)-var(--spacing-xxs))] pr-[calc(var(--spacing-m)-var(--spacing-xxs))]',
        iconPosition == null &&
            '[&:has(>svg:first-child)]:gap-xs [&:has(>svg:first-child)]:pl-[calc(var(--spacing-m)-var(--spacing-xxs))] [&:has(>svg:last-child)]:gap-xs [&:has(>svg:last-child)]:pr-[calc(var(--spacing-m)-var(--spacing-xxs))]',
    );

const getDefaultContentClasses = (iconPosition: ButtonIconPosition | undefined): string =>
    clsx(
        'py-m px-xl',
        iconPosition == null && 'gap-s',
        iconPosition === 'leading' && 'gap-s pl-l',
        iconPosition === 'trailing' && 'gap-s pr-l',
        iconPosition === 'both' && 'gap-s pl-l pr-l',
        iconPosition == null && '[&:has(>svg:first-child)]:pl-l [&:has(>svg:last-child)]:pr-l',
    );

const getDefaultRecessedContentClasses = (iconPosition: ButtonIconPosition | undefined): string =>
    clsx(
        'py-[calc(var(--spacing-m)-var(--spacing-xxs))] px-[calc(var(--spacing-xl)-var(--spacing-xxs))]',
        iconPosition == null && 'gap-s',
        iconPosition === 'leading' && 'gap-s pl-[calc(var(--spacing-l)-var(--spacing-xxs))]',
        iconPosition === 'trailing' && 'gap-s pr-[calc(var(--spacing-l)-var(--spacing-xxs))]',
        iconPosition === 'both' &&
            'gap-s pl-[calc(var(--spacing-l)-var(--spacing-xxs))] pr-[calc(var(--spacing-l)-var(--spacing-xxs))]',
        iconPosition == null &&
            '[&:has(>svg:first-child)]:pl-[calc(var(--spacing-l)-var(--spacing-xxs))] [&:has(>svg:last-child)]:pr-[calc(var(--spacing-l)-var(--spacing-xxs))]',
    );

const getSizeClasses = (
    size: ButtonProps['size'],
    iconOnly: boolean,
    iconPosition: ButtonIconPosition | undefined,
    variant: InternalButtonVariant,
): string => {
    switch (size) {
        case 'sm':
            return clsx(
                iconOnly ? '!rounded-full' : '!rounded-md',
                'text-xxs font-medium leading-[1rem]',
                '[&>svg]:!h-[12px] [&>svg]:!w-[12px]',
                iconOnly && variant !== 'link'
                    ? 'h-[var(--spacing-5xl)] w-[var(--spacing-5xl)] p-[calc(var(--spacing-s)+var(--spacing-xxs))]'
                    : iconOnly
                      ? 'p-[calc(var(--spacing-s)+var(--spacing-xxs))]'
                      : getSmContentClasses(iconPosition),
            );
        default:
            return clsx(
                'text-sm leading-[1.25rem]',
                iconOnly
                    ? 'h-[var(--spacing-7xl)] w-[var(--spacing-7xl)] p-[calc(var(--spacing-m)+var(--spacing-xxs))]'
                    : getDefaultContentClasses(iconPosition),
            );
    }
};

const getRecessedInnerSizeClasses = (
    size: ButtonProps['size'],
    iconOnly: boolean,
    iconPosition: ButtonIconPosition | undefined,
): string => {
    switch (size) {
        case 'sm':
            return clsx(
                iconOnly ? '!rounded-full' : '!rounded-recessed-control-sm',
                'text-xxs font-medium leading-[1rem]',
                '[&>svg]:!h-[12px] [&>svg]:!w-[12px]',
                iconOnly ? 'p-[var(--spacing-s)]' : getSmRecessedContentClasses(iconPosition),
            );
        default:
            return clsx(
                '!rounded-recessed-control text-sm leading-[1.25rem]',
                iconOnly
                    ? 'h-[calc(var(--spacing-7xl)-var(--spacing-xxs)*2)] w-[calc(var(--spacing-7xl)-var(--spacing-xxs)*2)] p-[var(--spacing-m)]'
                    : getDefaultRecessedContentClasses(iconPosition),
            );
    }
};

/* ============================================
 * Variant helpers (extracted to reduce complexity)
 * ============================================ */
const primaryShadowClass = 'shadow-primary-md hover:shadow-primary-lg';

const getPrimaryVariantClasses = (disabled: boolean, disabledVariant: ButtonProps['disabledVariant']): string => {
    const disabledBg = disabledVariant === 'dark' ? 'bg-black-900' : 'bg-gray-500';

    return clsx(
        'text-white-200',
        !disabled && primaryShadowClass,
        disabled ? disabledBg : 'bg-black-900',
        !disabled && 'hover:bg-black-800',
    );
};

const getSecondaryColorClasses = (disabled: boolean, isActive: boolean): string =>
    disabled
        ? 'bg-gray-200 shadow-none text-gray-800'
        : clsx(
              isActive ? 'bg-orange-100 text-orange-800 hover:text-orange-800' : 'bg-gray-100 text-gray-800',
              'shadow-sm hover:shadow-md',
          );

const getSecondaryRecessedInnerClasses = (disabled: boolean, isActive: boolean): string =>
    disabled
        ? 'bg-gray-200 shadow-none text-gray-800'
        : clsx(
              isActive
                  ? 'bg-gray-100 text-orange-800 hover:text-orange-800 shadow-sm'
                  : 'bg-gray-100 text-gray-800 shadow-sm',
          );

const getMutedColorClasses = (disabled: boolean): string =>
    disabled ? 'bg-gray-500 text-white-200' : 'bg-gray-500 text-white-200 hover:bg-black-900';

const getDestructiveVariantClasses = (disabled: boolean): string =>
    clsx('text-white-200', disabled ? 'bg-error-600' : 'bg-error-600 hover:bg-error-700');

const getTertiaryVariantClasses = (disabled: boolean, isActive: boolean): string =>
    clsx(getTertiaryPressedSurfaceClasses(isActive, disabled), isActive ? 'text-orange-800' : 'text-gray-900');

const getLinkVariantClasses = (disabled: boolean): string =>
    clsx(
        'bg-transparent no-underline',
        disabled ? 'text-gray-500' : 'text-black-900 hover:text-black-800 hover:no-underline',
        'py-xs px-s',
    );

/* ============================================
 * Variant classes
 * ============================================ */
const LEGACY_VARIANT_TO_PRESENTATION: Record<LegacyButtonVariant, InternalButtonVariant> = {
    default: 'primary',
    solid: 'primary',
};

const LEGACY_VARIANTS = Object.keys(LEGACY_VARIANT_TO_PRESENTATION) as LegacyButtonVariant[];

const isLegacyVariant = (variant: ButtonOwnProps['variant']): variant is LegacyButtonVariant =>
    variant != null && LEGACY_VARIANTS.includes(variant as LegacyButtonVariant);

const resolvePresentation = (
    variant: ButtonOwnProps['variant'],
    color: ButtonOwnProps['color'],
): InternalButtonVariant => {
    if (variant === 'solid') {
        switch (color) {
            case 'danger':
                return 'destructive';
            case 'muted':
                return 'muted';
            case 'secondary':
                return 'secondary';
            default:
                return 'primary';
        }
    }

    if (variant == null) {
        return color === 'danger'
            ? 'destructive'
            : color === 'secondary'
              ? 'secondary'
              : color === 'muted'
                ? 'muted'
                : 'primary';
    }

    if (isLegacyVariant(variant)) {
        return LEGACY_VARIANT_TO_PRESENTATION[variant];
    }

    return variant;
};

const getVariantClasses = (
    variant: InternalButtonVariant,
    disabled: boolean,
    disabledVariant: ButtonProps['disabledVariant'],
    isActive: boolean,
    iconOnly: boolean,
    size: ButtonProps['size'],
): string => {
    switch (variant) {
        case 'primary':
            return getPrimaryVariantClasses(disabled, disabledVariant);
        case 'destructive':
            return getDestructiveVariantClasses(disabled);
        case 'muted':
            return getMutedColorClasses(disabled);
        case 'secondary':
            return clsx(getSecondaryColorClasses(disabled, isActive), iconOnly && size === 'sm' && 'rounded-full!');
        case 'tertiary':
            return getTertiaryVariantClasses(disabled, isActive);
        case 'link':
            return getLinkVariantClasses(disabled);
    }
};

const baseClasses = clsx(
    'inline-flex items-center justify-center',
    'border-none rounded-lg outline-none',
    'font-sans font-medium no-underline',
    'transition-[background-color,color,box-shadow,opacity] duration-200 ease-in-out',
    '[&>svg]:h-[14px] [&>svg]:w-[14px] [&>svg]:shrink-0 [&>svg]:transform-gpu [&>svg]:[backface-visibility:hidden]',
    'focus-visible:outline focus-visible:outline-1 focus-visible:outline-orange-800',
);

export const Button = <E extends ElementType = 'button'>({
    activeSurface = 'recessed',
    as,
    children,
    className = '',
    color,
    disabled = false,
    disabledVariant,
    iconOnly = false,
    iconPosition,
    isActive = false,
    size,
    type = 'button',
    variant = 'solid',
    ...props
}: ButtonProps<E>): ReactElement => {
    const Component = as ?? 'button';
    const resolvedPresentation = resolvePresentation(variant, color);
    const shouldDimOnHover =
        resolvedPresentation !== 'primary' &&
        resolvedPresentation !== 'secondary' &&
        resolvedPresentation !== 'tertiary';
    const useRecessedActiveSurface = activeSurface === 'recessed' && resolvedPresentation === 'secondary' && !disabled;

    return (
        <Component
            className={clsx(
                baseClasses,
                useRecessedActiveSurface
                    ? clsx(
                          isActive
                              ? clsx(
                                    'overflow-hidden p-0 bg-transparent',
                                    size === 'sm' && (iconOnly ? '!rounded-full' : '!rounded-md'),
                                )
                              : clsx(
                                    getSizeClasses(size, iconOnly, iconPosition, resolvedPresentation),
                                    getSecondaryColorClasses(disabled, false),
                                ),
                      )
                    : clsx(
                          getSizeClasses(size, iconOnly, iconPosition, resolvedPresentation),
                          getVariantClasses(resolvedPresentation, disabled, disabledVariant, isActive, iconOnly, size),
                      ),
                disabled ? 'opacity-50 cursor-default' : 'cursor-pointer',
                !disabled && shouldDimOnHover && 'hover:opacity-80',
                className,
            )}
            disabled={disabled}
            type={Component === 'button' ? type : undefined}
            {...props}
        >
            {useRecessedActiveSurface && isActive ? (
                <RecessedSurface
                    active={true}
                    as="span"
                    className={size === 'sm' ? (iconOnly ? '!rounded-full' : '!rounded-md') : '!rounded-lg'}
                    placement="inside"
                    pocketTone="orange"
                    surfaceClassName={clsx(
                        'items-center justify-center',
                        getRecessedInnerSizeClasses(size, iconOnly, iconPosition),
                        getSecondaryRecessedInnerClasses(disabled, isActive),
                    )}
                    surfaceShadow="sm"
                    surfaceTone="gray"
                >
                    {children}
                </RecessedSurface>
            ) : (
                children
            )}
        </Component>
    );
};
